import { ExternalLink } from 'lucide-react'
import type { ReferralAttribution, ReferralOwnerDetail } from '@manaro/contracts'
import { formatDate } from '@/shared/lib/format.ts'
import { Badge } from '@/shared/ui/badge.tsx'
import { Card, CardHeader } from '@/shared/ui/card.tsx'
import { EmptyState } from '@/shared/ui/states.tsx'
import { ACCOUNT_KIND_LABELS } from '../labels.ts'
import { AttributionsTable } from './attributions-table.tsx'

interface ReferralOwnerProps {
  owner: ReferralOwnerDetail
  onRevert: (attribution: ReferralAttribution) => void
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Card className="flex min-w-0 flex-col gap-1 px-2.5 py-2.5 sm:gap-1.5 sm:px-5 sm:py-4">
      <div className="text-[12px] text-ink-muted max-sm:truncate sm:text-[13px]">{label}</div>
      <div className="tabular font-display text-[20px] leading-none font-semibold tracking-[-0.02em] sm:text-[24px]">
        {value}
      </div>
    </Card>
  )
}

export function ReferralOwner({ owner, onRevert }: ReferralOwnerProps) {
  const title = owner.ownerName || owner.ownerEmail || owner.code
  const suspicious = owner.attributions.filter((item) => item.suspicious && item.status !== 'reverted').length

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="font-display text-[25px] leading-tight font-semibold tracking-[-0.02em] md:text-[30px]">
            {title}
          </h1>
          <Badge tone="outline">{ACCOUNT_KIND_LABELS[owner.ownerType]}</Badge>
          {suspicious > 0 && <Badge tone="warn">подозрительных: {suspicious}</Badge>}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px] text-ink-muted">
          {owner.ownerEmail && owner.ownerEmail !== title && <span>{owner.ownerEmail}</span>}
          <span className="font-mono text-ink">{owner.code}</span>
          <a
            href={owner.link}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 hover:text-brand hover:underline max-md:-my-3 max-md:py-3 max-md:[overflow-wrap:anywhere]"
          >
            {owner.link}
            <ExternalLink className="size-3.5" aria-hidden />
          </a>
          {owner.createdAt && <span>код создан {formatDate(owner.createdAt)}</span>}
        </div>
      </header>

      {/* Three across even on a phone, so the five counts don't take the whole first screen. */}
      <div className="grid grid-cols-3 gap-1.5 sm:gap-3 lg:grid-cols-5">
        <Stat label="Приглашено" value={owner.stats.invited} />
        <Stat label="Пользователей" value={owner.stats.users} />
        <Stat label="Бизнесов" value={owner.stats.businesses} />
        <Stat label="Активных" value={owner.stats.active} />
        <Stat label="Отменено" value={owner.stats.reverted} />
      </div>

      <Card className="overflow-hidden">
        <CardHeader title="Кого пригласил" className="mb-0 px-4 pt-4.5 pb-3.5 md:px-5" />
        {owner.attributions.length === 0 ? (
          <EmptyState title="Пока никого" description="Как только кто-то зарегистрируется по этому коду, он появится здесь." />
        ) : (
          <AttributionsTable attributions={owner.attributions} onRevert={onRevert} />
        )}
      </Card>
    </div>
  )
}
